import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Cookie, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CookiePolicyPage = () => {
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState({
    title: 'Cookie Policy',
    last_updated: 'February 2026',
    sections: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/legal/cookies`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.sections?.length > 0) {
            setPageData(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch cookie policy:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const defaultSections = [
    {
      title: '1. What Are Cookies',
      content: `Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences, analyzing site traffic, and enabling certain features.\n\nWe use both session cookies (which expire when you close your browser) and persistent cookies (which remain on your device until they expire or you delete them).`
    },
    {
      title: '2. How We Use Cookies',
      content: `We use cookies for the following purposes:\n\n• Essential Cookies: Required for the website to function properly, including authentication and security.\n\n• Performance Cookies: Help us understand how visitors interact with our website by collecting anonymous usage data.\n\n• Functionality Cookies: Remember your preferences and settings to enhance your experience.\n\n• Analytics Cookies: Allow us to track and analyze website usage to improve our services.`
    },
    {
      title: '3. Types of Cookies We Use',
      content: `Essential Cookies:\n• Session management\n• Security and authentication\n• Load balancing\n\nPerformance Cookies:\n• Google Analytics\n• Page load time tracking\n\nFunctionality Cookies:\n• Language preferences\n• User interface settings\n• Remember login details`
    },
    {
      title: '4. Managing Cookies',
      content: `You can control and manage cookies through your browser settings. Most browsers allow you to:\n\n• View what cookies are stored\n• Delete individual or all cookies\n• Block cookies from specific or all websites\n• Set preferences for certain types of cookies\n\nPlease note that disabling certain cookies may affect the functionality of our website.`
    },
    {
      title: '5. Third-Party Cookies',
      content: `Some cookies are placed by third-party services that appear on our pages. We use:\n\n• Google Analytics for website analytics\n• Social media plugins for sharing functionality\n\nThese third parties may use cookies according to their own privacy policies.`
    },
    {
      title: '6. Updates to This Policy',
      content: `We may update this Cookie Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any material changes by posting the new policy on our website.\n\nIf you have questions about our use of cookies, please contact us at privacy@getcare360.com.au`
    }
  ];

  const sections = pageData.sections?.length > 0 ? pageData.sections : defaultSections;

  return (
    <div className="min-h-screen bg-page-bg">
      <SEOHead 
        title="Cookie Policy | GetCare 360 - How We Use Cookies"
        description="Learn about how GetCare 360 uses cookies on our website. Understand your choices for managing cookies and tracking preferences."
        keywords="GetCare 360 cookies, cookie policy, website cookies, tracking preferences, NDIS software cookies"
        canonicalUrl="/cookie-policy"
        page="cookie-policy"
        noIndex={false}
      />
      <Header />
      
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary-navy via-primary-navy to-tech-blue pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Cookie className="text-white" size={32} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{pageData.title}</h1>
          <p className="text-white/70">Last updated: {pageData.last_updated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin h-8 w-8 text-tech-blue" />
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
              {sections.map((section, index) => (
                <div key={index}>
                  <h2 className="text-2xl font-bold text-primary-navy mb-4">{section.title}</h2>
                  <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CookiePolicyPage;
